import type Dexie from 'dexie';
import type { Transaction } from 'dexie';

/**
 * Versioned IndexedDB schema. Never edit a released version: add a new one with
 * an `upgrade` function. Dexie runs every pending upgrade in order when the app
 * opens, so a device that skipped several releases is migrated step by step.
 *
 * Index notation: `&` unique, `[a+b]` compound, first entry = primary key.
 */
export interface DbVersion {
  version: number;
  description: string;
  stores: Record<string, string | null>;
  upgrade?: (tx: Transaction) => Promise<void>;
}

export const DB_VERSIONS: DbVersion[] = [
  {
    version: 1,
    description:
      'Initial schema: learners, sessions, attempts, mastery cache, achievements, settings.',
    stores: {
      learners: 'id, createdAt',
      sessions: 'id, learnerId, [learnerId+startedAt], status',
      attempts: 'id, learnerId, sessionId, [learnerId+objectiveId], [learnerId+answeredAt]',
      mastery: '[learnerId+objectiveId], learnerId',
      achievements: '[learnerId+achievementId], learnerId',
      meta: 'key',
    },
  },
  {
    version: 2,
    description:
      'Attempts carry an evidence weight (older attempts count fully) and sessions are indexed by learner and status for the parent dashboard.',
    stores: {
      sessions: 'id, learnerId, [learnerId+startedAt], [learnerId+status], status',
    },
    async upgrade(tx) {
      await tx
        .table('attempts')
        .toCollection()
        .modify((attempt: { evidenceWeight?: number }) => {
          if (typeof attempt.evidenceWeight !== 'number') attempt.evidenceWeight = 1;
        });
    },
  },
  {
    version: 3,
    description:
      'Feedback given by children inside games (ratings and problem reports), reviewed and sent by parents.',
    stores: {
      feedback: 'id, learnerId, createdAt',
    },
  },
];

export const CURRENT_DB_VERSION = DB_VERSIONS.at(-1)!.version;

export function applyMigrations(db: Dexie, versions: DbVersion[] = DB_VERSIONS): void {
  for (const v of versions) {
    const declared = db.version(v.version).stores(v.stores);
    if (v.upgrade) declared.upgrade(v.upgrade);
  }
}
